import { Pipe, PipeTransform } from '@angular/core';
import { CaseFormatService, CaseType } from '../services/case-format.service';

@Pipe({
    name: 'textCase',
    pure: false,
    standalone: true
})
export class TextCasePipe implements PipeTransform {

    constructor(private caseService: CaseFormatService) { }

    transform(value: string, overrideCase?: CaseType): string {
        if (!value) return '';

        const caseType = overrideCase || this.caseService.currentCase;

        switch (caseType) {
            case CaseType.SENTENCE:
                return this.toSentenceCase(value);

            case CaseType.TITLE:
                return this.toTitleCase(value);

            case CaseType.CAMEL:
                return this.toCamelCase(value);

            case CaseType.PASCAL:
                return this.toPascalCase(value);

            case CaseType.UPPER:
                return value.toUpperCase();

            case CaseType.LOWER:
                return value.toLowerCase();

            default:
                return value;
        }
    }

    private toSentenceCase(str: string): string {
        // Normalize whitespace
        str = str.trim().replace(/\s+/g, ' ');

        // Convert everything to lower-case
        str = str.toLowerCase();

        // Uppercase the first letter of string
        return str.charAt(0).toUpperCase() + str.slice(1);
    }

    private toTitleCase(str: string): string {
        return str
            .trim()
            .toLowerCase()
            // Capitalize letters after spaces, newlines, tabs, hyphens, parentheses, quotes
            .replace(/(^|\s|[\n\t\r()-_])/g, (m) => m)
            .replace(/\b\w/g, (c) => c.toUpperCase());
    }

    private toCamelCase(str: string): string {
        return str
            .trim()
            .toLowerCase()
            // Remove separators & capitalize the following letter
            .replace(/[-_\s]+(.)/g, (_, c) => c.toUpperCase());
    }

    private toPascalCase(str: string): string {
        const camel = this.toCamelCase(str);
        return camel.charAt(0).toUpperCase() + camel.slice(1);
    }
}

// EXAMPLES

// -------------------------------------------
// 1️⃣ SENTENCE CASE — Full Examples
// -------------------------------------------

// Input	Output
// "PROJECT DOCUMENT"	Project document
// " hello WORLD "	Hello world
// "hello WORLD"	Hello world
// "project-DOCUMENT"	Project-document
// "project_DOCUMENT"	Project_document
// "12TH DOCUMENT NAME"	12th document name
// "@TEST NAME"	@test name
// "PROJECT\nDOCUMENT"	Project\ndocument
// "JACK'S CAR"	Jack's car
// "Project document"	Project document
// "" / null / undefined	""

// -------------------------------------------
// 2️⃣ TITLE CASE — Full Examples
// -------------------------------------------

// Input	Output
// "hello world"	Hello World
// "project-document name"	Project-Document Name
// "project_document name"	Project_Document Name
// "jack's big car"	Jack's Big Car
// "hello\nworld"	Hello\nWorld
// " TESTING TITLE CASE "	Testing Title Case
// "12th world name"	12th World Name

// -------------------------------------------
// 3️⃣ CAMEL CASE — Full Examples
// -------------------------------------------

// Input	Output
// "hello world"	helloWorld
// "PROJECT DOCUMENT"	projectDocument
// "user_name_here"	userNameHere
// "user-name-here"	userNameHere
// "user name here"	userNameHere
// "12th world"	12thWorld
// "jack's car"	jack'sCar

// -------------------------------------------
// 4️⃣ PASCAL CASE — Full Examples
// -------------------------------------------

// Input	Output
// "hello world"	HelloWorld
// "PROJECT DOCUMENT"	ProjectDocument
// "user_name_here"	UserNameHere
// "user-name-here"	UserNameHere
// "12th world"	12thWorld
// "jack's car"	Jack'sCar

// -------------------------------------------
// 5️⃣ UPPERCASE — Full Examples
// -------------------------------------------

// Input	Output
// "hello World"	HELLO WORLD
// "jack's car"	JACK'S CAR
// "12th test"	12TH TEST
// "hello-name_here"	HELLO-NAME_HERE

// -------------------------------------------
// 6️⃣ LOWERCASE — Full Examples
// -------------------------------------------

// Input	Output
// "Hello World"	hello world
// "Jack's Car"	jack's car
// "HELLO-NAME_HERE"	hello-name_here